import { useState } from 'react';
import {
	ArrowLeft,
	LayoutGrid,
	LogOut,
	Menu,
	PanelsTopLeft,
} from 'lucide-react';
import { Button } from './Button';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '@/context/SidebarContext';
import { useGetMyCompany } from '@/services/queries/company';
import { cn } from '@/lib/utils';
import { E2E_HEADER_USER } from '@/constants/e2eSchedulingTestIds';
import type { VideoCallHeaderConfig } from '@/constants/sessionCall';
import { SessionCallMeetingTimer } from '@/components/session-call/SessionCallMeetingTimer';
import { UserHeaderProfileMenu } from './UserHeaderProfileMenu';

interface HeaderProps {
	showLoggout?: boolean;
	/** Fluxo público de agendamento antes do login: sem hamburger / sidebar. */
	hideSidebarMenu?: boolean;
	showCompanyBanner?: boolean;
	/** Menu de perfil no header (área logada do usuário). */
	showUserProfileMenu?: boolean;
	/** Modo dedicado para videochamada: sem sidebar e sem itens clicáveis no header. */
	videoCallMode?: boolean;
	videoCallHeaderConfig?: VideoCallHeaderConfig | null;
	/** Quando definido (ex.: portal empresa), exibe este banner/nome sem GET /companies/my-company */
	companyBannerOverride?: { url?: string | null; name?: string | null };
}

const Header = ({
	showLoggout,
	hideSidebarMenu = false,
	showCompanyBanner,
	showUserProfileMenu = false,
	videoCallMode,
	videoCallHeaderConfig,
	companyBannerOverride,
}: HeaderProps) => {
	const [companyImgError, setCompanyImgError] = useState(false);
	const logoProfessional =
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986600/Plumaa_H_W_hribid.png';
	const { toggleSidebar, handleLogout } = useSidebar();
	const navigate = useNavigate();
	const useMyCompanyQuery =
		!!showCompanyBanner && companyBannerOverride === undefined;
	const { data: myCompany } = useGetMyCompany({
		enabled: useMyCompanyQuery,
	});

	const companyImgUrl =
		companyBannerOverride !== undefined
			? companyBannerOverride.url ?? null
			: myCompany?.banner_img_url ?? myCompany?.logo_img_url ?? null;
	const companyDisplayName =
		companyBannerOverride !== undefined
			? companyBannerOverride.name ?? ''
			: myCompany?.trade_name ??
			  (myCompany as { legal_name?: string | null } | null)
					?.legal_name ??
			  (myCompany as { name?: string | null } | null)?.name ??
			  '';
	const companyHeaderTitle =
		companyDisplayName.trim().split(/\s+/)[0] ?? companyDisplayName;

	const companyBannerSlotClass =
		'relative h-11 max-w-[min(42vw,20rem)] min-w-[8rem] shrink-0 overflow-hidden rounded-md border border-white/40 sm:max-w-md';

	const showCompanyStrip =
		showCompanyBanner || companyBannerOverride !== undefined;
	const companyStripContent =
		showCompanyStrip &&
		(companyImgUrl && !companyImgError ? (
			<div className={cn('group', companyBannerSlotClass)}>
				<img
					src={companyImgUrl}
					alt={companyDisplayName || 'Banner da empresa'}
					className="h-full w-full object-cover object-center"
					onError={() => setCompanyImgError(true)}
				/>
				{companyDisplayName ? (
					<div className="absolute inset-x-0 bottom-0 flex h-5 translate-y-6 items-center justify-center bg-black/55 opacity-0 transition-all duration-200 ease-in-out group-hover:translate-y-0 group-hover:opacity-100">
						<span className="truncate px-2 text-[10px] font-semibold uppercase leading-none text-white">
							{companyDisplayName}
						</span>
					</div>
				) : null}
			</div>
		) : companyHeaderTitle ? (
			<div
				className={cn(
					companyBannerSlotClass,
					'flex items-center justify-center bg-white/10 px-3',
				)}
			>
				<span className="truncate text-xs font-semibold uppercase text-white">
					{companyHeaderTitle}
				</span>
			</div>
		) : null);

	const resolvedVideoCallHeaderConfig = videoCallHeaderConfig ?? {
		onBack: () => undefined,
		onToggleLayout: () => undefined,
		activeSpeakerMode: true,
		controlsEnabled: false,
	};
	const showVideoCallLayoutToggle =
		resolvedVideoCallHeaderConfig.showLayoutToggle !== false;
	const showVideoCallBackButton =
		resolvedVideoCallHeaderConfig.hideBackButton !== true;
	const meetingScheduledStartMs =
		resolvedVideoCallHeaderConfig.meetingScheduledStartMs ?? null;
	const showMeetingTimer =
		meetingScheduledStartMs !== null &&
		resolvedVideoCallHeaderConfig.hideMeetingTimer !== true;

	const videoCallMeetingTimer = showMeetingTimer ? (
		<SessionCallMeetingTimer scheduledStartMs={meetingScheduledStartMs} />
	) : null;

	const videoCallBackButton = (
		<button
			type="button"
			data-tutorial-demo="header-back"
			onClick={resolvedVideoCallHeaderConfig.onBack}
			className="rounded-full p-2 text-white transition-colors hover:bg-stone-800 focus:outline-none"
			aria-label="Voltar"
		>
			<ArrowLeft size={24} strokeWidth={2.5} />
		</button>
	);

	const videoCallLogo = resolvedVideoCallHeaderConfig.onLogoClick ? (
		<button
			type="button"
			data-tutorial-demo="header-logo"
			onClick={resolvedVideoCallHeaderConfig.onLogoClick}
			className="shrink-0 rounded-md transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
			aria-label="Voltar para a home"
		>
			<img
				src={logoProfessional}
				className="h-8 w-auto object-contain md:h-10"
				alt="Logotipo Plumaa"
			/>
		</button>
	) : (
		<img
			data-tutorial-demo="header-logo"
			src={logoProfessional}
			className="h-8 w-auto shrink-0 object-contain md:h-10"
			alt="Logotipo Plumaa"
		/>
	);

	return (
		<header
			className={cn(
				'relative z-50 flex h-16 items-center justify-between bg-stone-900 px-4',
			)}
		>
			{videoCallMode ? (
				<>
					<div className="flex w-full items-center justify-between gap-3 md:hidden">
						<div className="flex min-w-0 items-center gap-2">
							{showVideoCallBackButton
								? videoCallBackButton
								: null}
							{videoCallLogo}
						</div>

						<div className="flex shrink-0 items-center gap-2">
							{videoCallMeetingTimer}
							{showVideoCallLayoutToggle ? (
								<button
									type="button"
									onClick={
										resolvedVideoCallHeaderConfig.onToggleLayout
									}
									disabled={
										!resolvedVideoCallHeaderConfig.controlsEnabled
									}
									className={cn(
										'flex size-11 items-center justify-center rounded-2xl border border-zinc-200 bg-white text-zinc-700 shadow-sm transition-colors hover:bg-zinc-50',
										!resolvedVideoCallHeaderConfig.controlsEnabled &&
											'cursor-not-allowed opacity-50',
									)}
									aria-label={
										resolvedVideoCallHeaderConfig.activeSpeakerMode
											? 'Trocar para vista em grade'
											: 'Trocar para vista do orador'
									}
								>
									{resolvedVideoCallHeaderConfig.activeSpeakerMode ? (
										<PanelsTopLeft className="size-4" />
									) : (
										<LayoutGrid className="size-4" />
									)}
								</button>
							) : null}
						</div>
					</div>

					<div className="hidden w-full items-center justify-between gap-4 md:flex">
						<div className="flex items-center gap-4">
							{showVideoCallBackButton
								? videoCallBackButton
								: null}
							{videoCallLogo}
						</div>

						<div className="flex min-w-0 flex-1 items-center justify-end gap-4">
							{videoCallMeetingTimer}
							{companyStripContent}
							{showVideoCallLayoutToggle ? (
								<button
									type="button"
									onClick={
										resolvedVideoCallHeaderConfig.onToggleLayout
									}
									disabled={
										!resolvedVideoCallHeaderConfig.controlsEnabled
									}
									className={cn(
										'flex size-10 shrink-0 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-700 shadow-sm transition-colors hover:bg-zinc-50',
										!resolvedVideoCallHeaderConfig.controlsEnabled &&
											'cursor-not-allowed opacity-50',
									)}
									aria-label={
										resolvedVideoCallHeaderConfig.activeSpeakerMode
											? 'Trocar para vista em grade'
											: 'Trocar para vista do orador'
									}
								>
									{resolvedVideoCallHeaderConfig.activeSpeakerMode ? (
										<PanelsTopLeft className="size-4" />
									) : (
										<LayoutGrid className="size-4" />
									)}
								</button>
							) : null}
						</div>
					</div>
				</>
			) : (
				<>
					<div className="flex items-center gap-4">
						{!showLoggout && !videoCallMode && !hideSidebarMenu && (
							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									toggleSidebar();
								}}
								className="rounded-full p-2 text-white transition-colors hover:bg-stone-800 focus:outline-none"
								aria-label="Abrir ou recolher o menu"
							>
								<Menu size={24} />
							</button>
						)}

						<div
							onClick={() => {
								if (videoCallMode) return;
								navigate('/');
							}}
							className={cn(
								'flex items-center',
								videoCallMode
									? 'cursor-default'
									: 'cursor-pointer',
							)}
						>
							<img
								src={logoProfessional}
								className="h-10 w-auto"
								alt="Logotipo Plumaa"
							/>
						</div>
					</div>

					<div className="flex min-w-0 flex-1 items-center justify-end gap-4">
						{companyStripContent}
						{showUserProfileMenu && <UserHeaderProfileMenu />}
						{showLoggout && (
							<Button
								onClick={handleLogout}
								variant="text"
								data-testid={E2E_HEADER_USER.LOGOUT}
								className="rounded-lg px-3 py-2 text-sm font-bold uppercase tracking-widest text-stone-400 transition-all hover:bg-stone-800 hover:text-white"
								text="Sair"
								endIcon={<LogOut className="size-4" />}
							/>
						)}
					</div>
				</>
			)}
		</header>
	);
};

export default Header;
